import { Modal } from '@trackman/web-shared-components';
import { ServiceTypes } from 'constants/ServiceTypes';
import { SessionMode } from 'enums/SessionMode';
import { SessionType } from 'enums/SessionType';
import { IRosterResponse } from 'interfaces/team/IRosterResponse';
import { ITeam } from 'interfaces/team/ITeam';
import { observer } from 'mobx-react-lite';
import LineupManager from 'models/setLineup/LineupManager';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { ConfigurationService } from 'services/ConfigurationService';
import { FeatureConfigService } from 'services/FeatureConfigService';
import { useInjection } from 'services/ioc/ioc.react';
import RulesetService from 'services/RulesetService';
import { ContextStore } from 'stores/ContextStore';
import { ContextualDataStore } from 'stores/ContextualDataStore';
import { LineupStore } from 'stores/LineupStore';
import { NotificationStore } from 'stores/NotificationStore';
import { SessionStore } from 'stores/SessionStore';
import { TaggingStore } from 'stores/Tagging/TaggingStore';
import { Ruleset } from '../../../enums/Ruleset';
import type { ICustomStrikeZone } from '../../../interfaces/session/ICustomStrikeZone';
import { IGameScheduleReference, ISessionMode } from '../../../interfaces/session/ISessionMode';
import { IStrikeZoneDefinition, StrikeZoneDefinitionEnum } from '../../../interfaces/session/IStrikeZoneDefinition';
import CreateGamePage from './components/CreateGamePage/CreateGamePage';
import DialogFooter from './components/DialogFooter/DialogFooter';
import { Dots } from './components/Dots/Dots';
import { Preferences } from './components/Preferences/Preferences';
import ScheduledGamePage from './components/ScheduledGamePage/ScheduledGamePage';
import SelectGameSettingsPage from './components/SelectGameSettingsPage/SelectGameSettingsPage';
import OfficialStrikeZone from './components/SelectStrikeZone/OfficialStrikeZone';
import SelectStrikeZone from './components/SelectStrikeZone/SelectStrikeZone';
import DialogHeader from './components/DialogHeader/DialogHeader';
import { getPrimaryButtonLabel, isFinalPage, shouldShowBackButton } from './components/DialogFooter/footerConfig';
import { Body, DotsBar } from './GameDialogStyles';
import { Page } from './Page';

export { Page };

export interface ITeamExclusiveOptions {
  leagueId: string;
  leagueName: string;
  gameLevelId: string;
  gameLevelName: string;
}

interface IProps {
  visible: boolean;
  dismissCallback: () => void;
  strikeZones: IStrikeZoneDefinition[];
  gameScheduleReference?: IGameScheduleReference;
  isResumeGame: boolean;
}

export const GameDialog: React.FC<IProps> = observer((props) => {
  const { visible, dismissCallback, gameScheduleReference, isResumeGame, strikeZones } = props;
  const isScheduledGame = !!gameScheduleReference;
  const lineupStore = useInjection<LineupStore>(ServiceTypes.LineupStore);
  const sessionStore = useInjection<SessionStore>(ServiceTypes.SessionStore);
  const contextualDataStore = useInjection<ContextualDataStore>(ServiceTypes.ContextualDataStore);
  const configurationService = useInjection<ConfigurationService>(ServiceTypes.ConfigurationService);
  const [isPrivateGame, setIsPrivateGame] = useState<boolean | null>(null);
  const [page, setPage] = useState<Page>(Page.GameType);
  const [headline, setHeadline] = useState<string>('');
  const [teamExclusive, setTeamExclusive] = useState<ITeamExclusiveOptions>({
    gameLevelId: '',
    gameLevelName: '',
    leagueId: '',
    leagueName: '',
  });
  const featureConfigService = useInjection<FeatureConfigService>(ServiceTypes.FeatureConfigService);
  const [displayOrgWarning, setDisplayOrgWarning] = useState<boolean>(false);
  const history = useHistory();

  useEffect(() => {
    const config = configurationService.getConfigs();
    setTeamExclusive({
      gameLevelId: config.teamExclusiveGameLevelId,
      gameLevelName: config.teamExclusiveGameLevelName,
      leagueId: config.teamExclusiveLeagueId,
      leagueName: config.teamExclusiveLeagueName,
    });
  }, []);

  useEffect(() => {
    if (!visible) {
      return;
    }
    if (isResumeGame) {
      setIsPrivateGame(sessionStore.currentSessionMode.privateSession);
      lineupStore.setIsVideoCaptureEnabled(sessionStore.currentSessionMode.isVideoCaptureEnabled);
      lineupStore.setIsPlayerTrackingEnabled(sessionStore.currentSessionMetadata.isPlayerTrackingEnabled);
      lineupStore.setStrikeZone(sessionStore.currentSessionMetadata.strikeZone);
    }

    if (isScheduledGame && gameScheduleReference) {
      const league = contextualDataStore.leagues.find((league) => league.id === gameScheduleReference.league.id);

      lineupStore.setLeague(league ? league : gameScheduleReference.league);
      lineupStore.setGameLevel(gameScheduleReference.level);
      lineupStore.setIsVideoCaptureEnabled(featureConfigService.getConfig().isUploadVideoEnabled);
      lineupStore.setIsPlayerTrackingEnabled(featureConfigService.getConfig().isPlayerTrackingEnabled);
    } else if (
      sessionStore.currentSessionMetadata.league &&
      sessionStore.currentSessionMetadata.level &&
      sessionStore.currentSessionMetadata.owningOrganizationId
    ) {
      const league = contextualDataStore.leagues.find((league) => league.id === sessionStore.currentSessionMetadata.league?.id);
      lineupStore.setLeague(league ? league : sessionStore.currentSessionMetadata.league);
      lineupStore.setGameLevel(sessionStore.currentSessionMetadata.level);
      lineupStore.setOwningOrganization(
        contextualDataStore.owningOrganizations.find((org) => org.id === sessionStore.currentSessionMetadata.owningOrganizationId) || {}
      );
    } else {
      lineupStore.setLeague({});
      lineupStore.setGameLevel({});
    }

    if (!featureConfigService.getConfig().isNetworkedSystem) {
      setIsPrivateGame(true);
    }

    if (isPrivateGame) {
      const config = configurationService.getConfigs();
      const teamExclusiveLeague = contextualDataStore.leagues.find((league) => league.id === config.teamExclusiveLeagueId);

      if (teamExclusiveLeague) {
        lineupStore.setLeague(teamExclusiveLeague);
      } else {
        lineupStore.setLeague({
          id: config.teamExclusiveLeagueId,
          name: config.teamExclusiveLeagueName,
        });
      }
      lineupStore.setGameLevel({
        id: config.teamExclusiveGameLevelId,
        name: config.teamExclusiveGameLevelName,
      });
    }

    lineupStore.setRuleset(Ruleset.americanLeague);
  }, [visible]);

  useEffect(() => {
    if (isScheduledGame) {
      return;
    }
    if (isPrivateGame) {
      const teamExclusiveLeague = contextualDataStore.leagues.find((league) => league.id === teamExclusive.leagueId);

      if (teamExclusiveLeague) {
        lineupStore.setLeague(teamExclusiveLeague);
      } else {
        lineupStore.setLeague({
          id: teamExclusive.leagueId,
          name: teamExclusive.leagueName,
        });
      }

      lineupStore.setGameLevel({
        id: teamExclusive.gameLevelId,
        name: teamExclusive.gameLevelName,
      });
    } else {
      if (!isResumeGame) {
        lineupStore.setLeague({});
        lineupStore.setGameLevel({});
        lineupStore.setOwningOrganization({});
      }
    }
  }, [isPrivateGame]);

  useEffect(() => {
    switch (page) {
      case Page.GameType:
        if (!isScheduledGame) {
          setHeadline(isResumeGame ? 'Resume game' : 'Create game');
        } else {
          setHeadline('Confirm teams');
        }
        return;
      case Page.GameSettings:
      case Page.StrikeZone:
      case Page.SessionData:
        setHeadline('Set up game');
        return;
    }
  }, [page, isScheduledGame]);

  const handleOnDissmiss = async () => {
    setPage(Page.GameType);
    setIsPrivateGame(false);
    dismissCallback();
  };

  const proceedToLineup = () => {
    contextualDataStore.setIsGameDialogVisible(false);
    history.push('/set-lineup');
  };

  const taggingStore = useInjection<TaggingStore>(ServiceTypes.TaggingStore);
  const contextStore = useInjection<ContextStore>(ServiceTypes.ContextStore);

  const changeSessionMode = async (): Promise<void> => {
    const sessionData: ISessionMode = {
      sessionMode: SessionMode.SessionStarted,
      sessionType: SessionType.Standard,
      systemId: contextStore.activeField!.systemId,
      time: '', // will be set in store anyway
      strikeZone: lineupStore.strikeZone,
      isVideoCaptureEnabled: lineupStore.isVideoCaptureEnabled,
      isPlayerTrackingEnabled: lineupStore.isPlayerTrackingEnabled,
      issuer: 'TaggingApp',
      ...sessionStore.initialMetadata!,
    };

    if (featureConfigService.getConfig().isRoboUmpEnabled || lineupStore.strikeZone?.name === 'NCAA') {
      sessionData.strikeZone = lineupStore.strikeZone;
    } else {
      sessionData.strikeZone = undefined;
    }

    await sessionStore.setCurrentSessionMode(sessionData);
  };
  const rulesetService = useInjection<RulesetService>(ServiceTypes.RulesetService);

  const createLineupManager = (team: ITeam, roster: IRosterResponse | undefined): LineupManager => {
    const activeStrikeZone =
      lineupStore.overridedStrikeZone?.activeStrikeZone ||
      sessionStore.currentSessionMetadata.strikeZone?.customDefinition?.activeStrikeZone ||
      sessionStore.currentSessionMetadata.strikeZone?.activeStrikeZone;

    const manager = new LineupManager(
      team,
      lineupStore.ruleset,
      roster?.players || [],
      activeStrikeZone,
      rulesetService.getPlayerLimit(lineupStore.ruleset),
      undefined,
      sessionStore.currentSessionMode.privateSession,
      sessionStore.currentSessionMode.privateSession
    );

    manager.autoFillLineUp(roster?.lineup || []);
    return manager;
  };

  const startAutoTagging = async (): Promise<void> => {
    let customStrikeZone: ICustomStrikeZone | null = null;

    if (featureConfigService.getConfig().isRoboUmpEnabled && sessionStore.currentSessionMode.privateSession) {
      customStrikeZone = {
        definition: await lineupStore.getOverridedStrikeZoneForSessionMetadata(),
        locations: lineupStore.getPlaneLocationsForSessionMetadata(),
      };
    }

    if (gameScheduleReference) {
      const homeTeam = createLineupManager(gameScheduleReference?.homeTeam, undefined);
      const awayTeam = createLineupManager(gameScheduleReference?.awayTeam, undefined);
      await changeSessionMode().then(async () => {
        await taggingStore
          .initializeFromLineup(
            homeTeam,
            awayTeam,
            gameScheduleReference.level,
            gameScheduleReference.league,
            lineupStore.ruleset,
            customStrikeZone,
            lineupStore.owningOrganization.id
            // selectedUmpire
          )
          .then(async () => {
            await sessionStore.setCurrentSessionMode({
              ...sessionStore.currentSessionMode,
              sessionMode: SessionMode.WarmupStarted,
            });
          })
          .then(async () => {
            await sessionStore.setCurrentSessionMode({
              ...sessionStore.currentSessionMode,
              sessionMode: SessionMode.PlayingStarted,
            });
          });
      });
    }
  };

  const notificationStore = useInjection<NotificationStore>(ServiceTypes.NotificationStore);

  const handleOnClick = async (): Promise<void> => {
    sessionStore.setInitialMetadata({
      externalSessionId: gameScheduleReference ? gameScheduleReference.externalSessionId : '',
      gameScheduleReference,
      privateSession: isPrivateGame!,
    });

    if (sessionStore.isOperatorlessTaggingEnabledForSelectedSession) {
      startAutoTagging().then(() => {
        handleOnDissmiss();
        history.push('/');
        notificationStore.addToast({
          header: 'Game started!',
          message: `The game between ${gameScheduleReference?.homeTeam.name} and ${gameScheduleReference?.awayTeam.name} has started. Auto-tag is enabled.`,
          severity: 'success',
          open: true,
          onClose: () => {
            notificationStore.removeToast();
          },
        });
      });
    } else {
      proceedToLineup();
    }
  };

  const [leagueError, setLeagueError] = useState(false);
  const [levelError, setLevelError] = useState(false);

  useEffect(() => {
    setLeagueError(lineupStore.league.id === '');
    setLevelError(lineupStore.gameLevel.id === '');
  }, [lineupStore.league, lineupStore.gameLevel]);

  const isStrikeZoneValuesFilled = () => {
    if (page !== Page.StrikeZone || !isPrivateGame) {
      return true;
    }

    let status = true;

    if (lineupStore.overridedStrikeZone) {
      const validateValue = (value: any) => {
        return value === null || Number(value) <= 0 || value === undefined || isNaN(Number(value));
      };

      if (validateValue(lineupStore.overridedStrikeZone.ballDiameter)) {
        status = false;
      }

      if (featureConfigService.getConfig().isRoboUmpEnabled) {
        switch (lineupStore.overridedStrikeZone.activeStrikeZone) {
          case StrikeZoneDefinitionEnum.HeightBased:
            if (
              validateValue(lineupStore.overridedStrikeZone.heightStrikeZoneTopPercentage) ||
              validateValue(lineupStore.overridedStrikeZone.heightStrikeZoneBottomPercentage) ||
              validateValue(lineupStore.overridedStrikeZone.heightStrikeZoneWidth)
            ) {
              status = false;
            }
            break;
          case StrikeZoneDefinitionEnum.Static:
            if (
              validateValue(lineupStore.overridedStrikeZone.staticStrikeZoneTop) ||
              validateValue(lineupStore.overridedStrikeZone.staticStrikeZoneBottom) ||
              validateValue(lineupStore.overridedStrikeZone.staticStrikeZoneWidth)
            ) {
              status = false;
            }
            break;

          case StrikeZoneDefinitionEnum.TopAndBottomBased:
            if (validateValue(lineupStore.overridedStrikeZone.topBottomStrikeZoneWidth)) {
              status = false;
            }
            break;
        }
      }
    }

    return status;
  };

  const disableNextButton = (): boolean => {
    if (page === Page.GameType) {
      return isPrivateGame === null && !isScheduledGame;
    }

    if (page === Page.StrikeZone) {
      return !!isPrivateGame && lineupStore.planeConfiguration.filter((plane) => plane.isEnabled).length === 0;
    }

    return (
      (!lineupStore.league.id && !lineupStore.league.name) ||
      (!lineupStore.gameLevel.id && !lineupStore.gameLevel.name) ||
      lineupStore.isLoading ||
      !isStrikeZoneValuesFilled()
    );
  };

  const disableCreateGame = (): boolean => {
    return (
      !lineupStore.league ||
      !lineupStore.gameLevel ||
      (lineupStore.league && !lineupStore.league.id && !lineupStore.league.name) ||
      (lineupStore.gameLevel && !lineupStore.gameLevel.id && !lineupStore.gameLevel.name)
    );
  };

  const getPageContent = (page: Page): JSX.Element => {
    switch (page) {
      case Page.GameType:
        return isScheduledGame ? (
          <ScheduledGamePage
            awayTeam={gameScheduleReference!.awayTeam}
            homeTeam={gameScheduleReference!.homeTeam}
            setIsPrivateGame={setIsPrivateGame}
          />
        ) : (
          <CreateGamePage
            isPrivateGameCallback={(value: boolean): void => setIsPrivateGame(value)}
            isPrivateGame={isPrivateGame}
            isResumeGame={isResumeGame}
          />
        );
      case Page.SessionData:
        return (
          <SelectGameSettingsPage
            isScheduledGame={isScheduledGame}
            isPrivateGame={isPrivateGame!}
            strikeZones={strikeZones}
            leagueError={leagueError}
            levelError={levelError}
            setLeagueError={setLeagueError}
            setLevelError={setLevelError}
            displayOrgWarning={displayOrgWarning}
          />
        );
      case Page.StrikeZone:
        if (isPrivateGame) {
          return <SelectStrikeZone isPrivateGame={isPrivateGame} />;
        }

        return <OfficialStrikeZone isPrivateGame={isPrivateGame!} />;
      case Page.GameSettings:
        return <Preferences isPrivateGame={isPrivateGame!} isResumeGame={isResumeGame} />;
      default:
        return <></>;
    }
  };

  useEffect(() => {
    if (lineupStore.owningOrganization.id && lineupStore.owningOrganization.id !== '') {
      setDisplayOrgWarning(false);
    }
  }, [lineupStore.owningOrganization]);

  useEffect(() => {
    if (page === Page.GameType) {
      setDisplayOrgWarning(false);
    }
  }, [page]);

  useEffect(() => {
    return () => {
      sessionStore.setIsOperatorlessTaggingEnabledForSelectedSession(false);
      contextualDataStore.setIsHomePageGameDialogVisible(false);
    };
  }, []);

  const handleNextPage = () => {
    if (leagueError || levelError) {
      return;
    }

    setPage((page) => {
      switch (page) {
        case Page.GameType:
          return Page.SessionData;
        case Page.SessionData:
          if (!displayOrgWarning && !lineupStore.owningOrganization.id) {
            setDisplayOrgWarning(true);
            return Page.SessionData;
          } else {
            return !featureConfigService.getConfig().isRoboUmpEnabled ? Page.GameSettings : Page.StrikeZone;
          }
        case Page.StrikeZone:
          return Page.GameSettings;
      }

      return Page.GameType;
    });
  };

  const handlePreviousPage = () => {
    setPage((page) => {
      switch (page) {
        case Page.SessionData:
          return Page.GameType;
        case Page.StrikeZone:
          return Page.SessionData;
        case Page.GameSettings:
          return !featureConfigService.getConfig().isRoboUmpEnabled ? Page.SessionData : Page.StrikeZone;
      }

      return Page.GameType;
    });
  };

  const getPages = () => {
    if (featureConfigService.getConfig().isRoboUmpEnabled) {
      return Object.keys(Page).filter((key) => isNaN(Number(key)));
    }

    return Object.keys(Page).filter((key) => {
      return isNaN(Number(key)) && key !== 'StrikeZone';
    });
  };

  const isCurrentPageFinal = isFinalPage(page);

  return (
    <Modal
      open={visible}
      onClose={handleOnDissmiss}
      isCustom
      maxWidth='lg'
      fullWidth
      PaperProps={{
        style: {
          width: '960px',
          height: 'min(692px, calc(100vh - 64px))',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <DialogHeader title={headline} onClose={handleOnDissmiss} />
      <Body>{getPageContent(page)}</Body>
      <DotsBar>
        <Dots pages={getPages()} currentPage={page} />
      </DotsBar>
      <DialogFooter
        showBack={shouldShowBackButton(page)}
        primaryLabel={getPrimaryButtonLabel({
          page,
          isScheduledGame,
          isAutoTaggingEnabled: sessionStore.isOperatorlessTaggingEnabledForSelectedSession,
        })}
        primaryDisabled={isCurrentPageFinal ? disableCreateGame() : disableNextButton()}
        onPrimary={isCurrentPageFinal ? handleOnClick : handleNextPage}
        onBack={handlePreviousPage}
        onCancel={handleOnDissmiss}
      />
    </Modal>
  );
});
